import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  Plus, Trash2, ChevronDown, ChevronUp, Star, Upload, X, User
} from 'lucide-react';
import { characterFields, defaultCharacter } from '../../data/templateCustomizationSchemas';

const ColorSwatch = ({ color, selected, onClick, label }) => (
  <button
    type="button" onClick={onClick}
    className={`group flex flex-col items-center gap-1 transition-all ${selected ? 'scale-110' : 'hover:scale-105'}`}
    title={label}
  >
    <div className={`w-8 h-8 rounded-full border-2 transition-all ${
      selected ? 'border-primary ring-2 ring-primary/30' : 'border-stone-200 group-hover:border-stone-400'
    }`} style={{ backgroundColor: color }} />
    <span className="text-[9px] font-sans text-stone-500 max-w-[48px] truncate">{label}</span>
  </button>
);

const ToggleField = ({ field, value, onChange }) => {
  const isActive = value?.enabled;
  return (
    <div className={`rounded-xl border p-3 transition-all ${isActive ? 'border-primary/40 bg-primary/5' : 'border-stone-200'}`}>
      <label className="flex items-center justify-between cursor-pointer">
        <span className="text-sm font-sans text-stone-700">{field.label}</span>
        <button
          type="button"
          onClick={() => onChange({ ...value, enabled: !isActive })}
          className={`w-10 h-6 rounded-full transition-all ${isActive ? 'bg-primary' : 'bg-stone-200'}`}
        >
          <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-5' : 'translate-x-1'}`} />
        </button>
      </label>
      {isActive && field.type === 'toggle-detail' && (
        <input
          type="text"
          value={value?.detail || ''}
          onChange={(e) => onChange({ ...value, detail: e.target.value })}
          placeholder={field.detailPlaceholder}
          className="mt-2 w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-lg focus:border-primary focus:outline-none bg-white"
        />
      )}
      {isActive && field.type === 'toggle-select' && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {field.options.map(opt => (
            <button key={opt.value} type="button"
              onClick={() => onChange({ ...value, detail: opt.value })}
              className={`text-xs font-sans px-2.5 py-1 rounded-full border transition-all ${
                value?.detail === opt.value ? 'bg-primary/10 border-primary text-primary' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CharacterCard = ({ character, index, isMain, onUpdate, onRemove, onSetMain }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const [showInclusive, setShowInclusive] = useState(false);

  const updateField = (fieldId, value) => {
    onUpdate(index, { ...character, [fieldId]: value });
  };

  const updateInclusive = (fieldId, value) => {
    onUpdate(index, {
      ...character,
      inclusiveFeatures: { ...character.inclusiveFeatures, [fieldId]: value }
    });
  };

  return (
    <div data-testid={`character-card-${index}`} className={`bg-white rounded-2xl border transition-all ${
      isMain ? 'border-primary/40 ring-1 ring-primary/20' : 'border-stone-200'
    }`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          isMain ? 'bg-primary/10' : 'bg-stone-100'
        }`}>
          {character.referenceImage ? (
            <img src={character.referenceImage} alt="" className="w-10 h-10 rounded-xl object-cover" />
          ) : (
            <User className={`w-5 h-5 ${isMain ? 'text-primary' : 'text-stone-400'}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-sans font-semibold text-stone-800 text-sm truncate">
              {character.name || `Karakter ${index + 1}`}
            </p>
            {isMain && (
              <span className="text-[9px] font-sans font-bold uppercase px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                Hovedperson
              </span>
            )}
          </div>
          <p className="text-xs font-sans text-stone-500">
            {characterFields.basic.find(f => f.id === 'role')?.options.find(o => o.value === character.role)?.label || 'Role not selected'}
            {character.age ? `, ${character.age} years` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {!isMain && (
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-stone-400 hover:text-primary"
              onClick={(e) => { e.stopPropagation(); onSetMain(index); }} title="Make primary person">
              <Star className="w-4 h-4" />
            </Button>
          )}
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-stone-400 hover:text-red-500"
            onClick={(e) => { e.stopPropagation(); onRemove(index); }} title="Remove karakter">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
          {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-5 border-t border-stone-100 pt-4">
          {/* Reference Image Upload */}
          <div>
            <p className="text-xs font-sans font-medium text-stone-600 mb-2">Referansebilde (optional)</p>
            {character.referenceImage ? (
              <div className="relative w-24 h-24">
                <img src={character.referenceImage} alt="Referanse" className="w-24 h-24 rounded-xl object-cover border border-stone-200" />
                <button type="button"
                  onClick={() => updateField('referenceImage', null)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-2 w-fit px-3 py-2 rounded-xl border border-dashed border-stone-300 hover:border-primary cursor-pointer transition-colors">
                <Upload className="w-4 h-4 text-stone-400" />
                <span className="text-xs font-sans text-stone-500">Upload image</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => updateField('referenceImage', ev.target.result);
                    reader.readAsDataURL(file);
                  }
                }} />
              </label>
            )}
            <p className="text-[10px] font-sans text-stone-400 mt-1">AI bruker bildet som visuell referanse for illustrasjoner</p>
          </div>

          {/* Basic Fields */}
          <div className="grid grid-cols-2 gap-3">
            {characterFields.basic.map(field => (
              <div key={field.id}>
                <label className="text-xs font-sans font-medium text-stone-600 mb-1 block">
                  {field.label} {field.required && <span className="text-red-400">*</span>}
                </label>
                {field.type === 'text' && (
                  <input type="text" value={character[field.id] || ''} onChange={(e) => updateField(field.id, e.target.value)}
                    placeholder={field.placeholder} className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl focus:border-primary focus:outline-none bg-stone-50" />
                )}
                {field.type === 'number' && (
                  <input type="number" value={character[field.id] || ''} onChange={(e) => updateField(field.id, e.target.value)}
                    placeholder={field.placeholder} min={field.min} max={field.max}
                    className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl focus:border-primary focus:outline-none bg-stone-50" />
                )}
                {field.type === 'select' && (
                  <select value={character[field.id] || ''} onChange={(e) => updateField(field.id, e.target.value)}
                    className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl focus:border-primary focus:outline-none bg-stone-50 appearance-none">
                    <option value="">Choose...</option>
                    {field.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>

          {/* Appearance Fields */}
          <div>
            <p className="text-xs font-sans font-semibold text-stone-700 mb-3 uppercase tracking-wide">Utseende</p>
            <div className="space-y-4">
              {characterFields.appearance.map(field => (
                <div key={field.id}>
                  <label className="text-xs font-sans font-medium text-stone-600 mb-2 block">{field.label}</label>
                  {field.type === 'color-select' && (
                    <div className="flex flex-wrap gap-2">
                      {field.options.map(opt => (
                        <ColorSwatch key={opt.value} color={opt.color} label={opt.label}
                          selected={character[field.id] === opt.value}
                          onClick={() => updateField(field.id, opt.value)} />
                      ))}
                    </div>
                  )}
                  {field.type === 'select' && (
                    <div className="flex flex-wrap gap-1.5">
                      {field.options.map(opt => (
                        <button key={opt.value} type="button"
                          onClick={() => updateField(field.id, opt.value)}
                          className={`text-xs font-sans px-3 py-1.5 rounded-full border transition-all ${
                            character[field.id] === opt.value ? 'bg-primary/10 border-primary text-primary' : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-stone-400'
                          }`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {field.type === 'text' && (
                    <input type="text" value={character[field.id] || ''} onChange={(e) => updateField(field.id, e.target.value)}
                      placeholder={field.placeholder} className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl focus:border-primary focus:outline-none bg-stone-50" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Inclusive Features - Expandable */}
          <div>
            <button type="button"
              onClick={() => setShowInclusive(!showInclusive)}
              className="flex items-center gap-2 text-xs font-sans font-semibold text-stone-500 hover:text-stone-700 transition-colors"
            >
              {showInclusive ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Flere valg for utseende
            </button>
            {showInclusive && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {characterFields.inclusive.map(field => {
                  if (field.type === 'textarea') {
                    return (
                      <div key={field.id} className="col-span-full">
                        <label className="text-xs font-sans font-medium text-stone-600 mb-1 block">{field.label}</label>
                        <textarea value={character.inclusiveFeatures?.[field.id] || ''}
                          onChange={(e) => updateInclusive(field.id, e.target.value)}
                          placeholder={field.placeholder} rows={2}
                          className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl focus:border-primary focus:outline-none bg-stone-50" />
                      </div>
                    );
                  }
                  return (
                    <ToggleField key={field.id} field={field}
                      value={character.inclusiveFeatures?.[field.id]}
                      onChange={(val) => updateInclusive(field.id, val)} />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const CharacterBuilder = ({ characters, onChange }) => {
  const addCharacter = () => {
    const isFirst = characters.length === 0;
    onChange([...characters, { ...defaultCharacter, role: isFirst ? 'main' : 'supporting' }]);
  };

  const removeCharacter = (index) => {
    if (characters.length <= 1) return;
    const next = characters.filter((_, i) => i !== index);
    if (!next.some(c => c.role === 'main') && next.length > 0) {
      next[0].role = 'main';
    }
    onChange(next);
  };

  const setMain = (index) => {
    onChange(characters.map((c, i) => ({
      ...c,
      role: i === index ? 'main' : (c.role === 'main' ? 'supporting' : c.role)
    })));
  };

  const updateCharacter = (index, updated) => onChange(characters.map((c, i) => i === index ? updated : c));

  return (
    <div data-testid="character-builder" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Karakterer</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Lag karakterene i historien din</p>
        </div>
        <Button type="button" onClick={addCharacter} variant="outline" size="sm"
          className="rounded-full text-xs font-sans gap-1.5 border-stone-200"
          disabled={characters.length >= 8}>
          <Plus className="w-3.5 h-3.5" /> Add karakter
        </Button>
      </div>

      <div className="space-y-3">
        {characters.map((char, i) => (
          <CharacterCard key={i} character={char} index={i}
            isMain={char.role === 'main'}
            onUpdate={updateCharacter} onRemove={removeCharacter} onSetMain={setMain} />
        ))}
      </div>

      {characters.length === 0 && (
        <button type="button" onClick={addCharacter}
          className="w-full py-10 border-2 border-dashed border-stone-200 rounded-2xl hover:border-primary/40 transition-colors text-center">
          <User className="w-8 h-8 text-stone-300 mx-auto mb-2" />
          <p className="text-sm font-sans text-stone-500">Click to add your first character</p>
        </button>
      )}
    </div>
  );
};
